import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import BaseTable from "@/components_v2/Tables/BaseTable";
import AnalyticsCard from "@/features/certifications/analytics/components/AnalyticsCard";
import { CARD_SIZES } from "@/features/certifications/analytics/constants";
import {
  getTrackComparisonRows,
  getTrackLabel,
  toPercent,
} from "@/features/certifications/analytics/utils";
import {
  TrackComparisonCardProps,
  TrackComparisonRow,
} from "@/features/certifications/analytics/types";

import "../styles.css";

const COUNT_COLUMNS = [
  "enrolled",
  "active",
  "notStarted",
  "inProgress",
  "awaitingReview",
  "certified",
  "certificatesIssued",
] as const;

const TrackComparisonCard = ({
  certification,
  data,
  stats,
  isLoading,
  isError,
}: TrackComparisonCardProps) => {
  const { t } = useTranslation();
  const prefix = "certification.admin.analytics.paths.comparison";

  const rows = useMemo(
    () =>
      data
        ? getTrackComparisonRows(data, stats, (trackId, fallback) =>
            getTrackLabel(t, certification, trackId, fallback),
          )
        : [],
    [certification, data, stats, t],
  );

  const columns = useMemo(
    () => [
      {
        key: "label",
        dataIndex: "label",
        title: t(`${prefix}.columns.path`),
      },
      ...COUNT_COLUMNS.map((key) => ({
        key,
        title: t(`${prefix}.columns.${key}`),
        align: "right" as const,
        render: (_: unknown, row: TrackComparisonRow) =>
          row[key] ?? t("charts.noValue"),
      })),
      {
        key: "completionRate",
        title: t(`${prefix}.columns.completionRate`),
        align: "right" as const,
        render: (_: unknown, row: TrackComparisonRow) =>
          row.completionRate === null
            ? t("charts.noValue")
            : t("charts.percent", { percent: toPercent(row.completionRate) }),
      },
      {
        key: "medianDays",
        title: t(`${prefix}.columns.medianDays`),
        align: "right" as const,
        render: (_: unknown, row: TrackComparisonRow) =>
          row.medianDaysToCertificate === null
            ? t("charts.noValue")
            : t(`${prefix}.days`, { count: row.medianDaysToCertificate }),
      },
    ],
    [t],
  );

  return (
    <AnalyticsCard
      title={t(`${prefix}.title`)}
      isLoading={isLoading}
      isError={isError}
      size={CARD_SIZES.FULL}
    >
      <BaseTable<TrackComparisonRow>
        dataSource={rows}
        columns={columns}
        rowKey="id"
        pagination={false}
        scroll={{ x: "max-content" }}
        locale={{ emptyText: t(`${prefix}.empty`) }}
      />
    </AnalyticsCard>
  );
};

export default TrackComparisonCard;
